import * as Sentry from "@sentry/nextjs"
import { Elements } from "@stripe/react-stripe-js"
import { CardNumberElement } from "@stripe/react-stripe-js"
import { loadStripe, PaymentIntent, SetupIntent, Stripe, StripeElements } from "@stripe/stripe-js"
import { Spinner } from "flowbite-react"
import { Form, FormikHelpers, FormikProps, Formik } from "formik"
import React, { useMemo, useRef, useState } from "react"
import useSWR from "swr"
import * as Yup from "yup"

import useAuth from "@hooks/useAuth"
import useToast from "@hooks/useToast"
import { CartItemDetailsType } from "@modules/tenants/OrdersPage/Components/AddOrderModal"
import * as OrderServices from "@mono/essentials/services/OrderServices"
import {
  ValidateGiftcardCodeResponseType,
  depositType,
  PaymentTypes,
} from "@mono/essentials/types/CartTypes"
import { CreateOrderRequestType, OrderStatuses } from "@mono/essentials/types/OrderTypes"
import {
  CustomerTypeOrder,
  PayOrderRequestType,
  GetOrderDetailsResponseType,
  OrderStatusTypes,
} from "@mono/essentials/types/OrderTypes"

import { PaymentAmountDetails } from "../PaymentModal"

import StripeCreditCardFormData from "./StripeCreditCardFormData"

export type StripeCreditCardFormAttributes = {
  name: string
  paymentType: PaymentTypes
  amount: string
  isUsingSavedCard?: boolean
  savedCardId: string
  hasPaymentValidData?: boolean
}

interface StripeCreditCardProps {
  handleClose: () => void
  onSuccess?: (orderId: string) => void
  stripeKey: string
  stripeAccountId: string
  isModal?: boolean
  hasDepositData?: depositType | undefined
  stripeCreditCardFormRef?: React.RefObject<FormikProps<StripeCreditCardFormAttributes>>
  hideDefaultContainer?: boolean
  cartId?: string
  orderId?: string
  stripePaymentId: string
  isIntegratedPage?: boolean
  onOrderCreation?: (orderData?: GetOrderDetailsResponseType) => void
  cartDetails?: CartItemDetailsType
  refetchCartData?: () => void
  paymentAmountDetails?: PaymentAmountDetails
  goToPrevStep?: () => void
  onError?: () => void
  hideBackButton?: boolean
  payOrderTipDetails?: PayOrderRequestType["tip"]
  onlyTipPayment?: boolean
  customer?: CustomerTypeOrder
  handleSubmit?: (
    arg0: StripeCreditCardFormAttributes,
    paymentToken: string,
    stripe: Stripe
  ) => Promise<void>
  hideSavedCards?: boolean
  isPublicPayment?: boolean
  isWidgetsCard?: boolean
  giftcardDetails?: ValidateGiftcardCodeResponseType["giftcard"]
  isAuthUser?: boolean
  showPendingBookingPaymentConfirmation?: (arg0: boolean) => void
  handlePaymentConfirmation?: boolean
  handleCustomerRebookDuePayment?: (payload: PayOrderRequestType) => Promise<void>
  isWalletsEnabled?: boolean
}

let stripePromise: Stripe | null = null
const getStripe = async (stripeKey: string) => {
  if (!stripePromise) {
    stripePromise = await loadStripe(stripeKey)
  }
  return stripePromise
}

const StripeCreditCard = ({
  handleClose,
  onSuccess,
  stripeKey,
  stripeCreditCardFormRef,
  hideDefaultContainer,
  cartId,
  stripePaymentId,
  isIntegratedPage,
  onOrderCreation,
  cartDetails,
  refetchCartData,
  goToPrevStep,
  paymentAmountDetails,
  onError,
  hideBackButton,
  orderId: postPaymentOrderId,
  payOrderTipDetails,
  onlyTipPayment = false,
  customer,
  handleSubmit,
  isWidgetsCard = false,
  hideSavedCards = false,
  isPublicPayment = false,
  giftcardDetails,
  isAuthUser = true,
  showPendingBookingPaymentConfirmation,
  handlePaymentConfirmation = false,
  handleCustomerRebookDuePayment,
  isWalletsEnabled = false,
}: StripeCreditCardProps) => {
  const { publicDomainDetails } = useAuth()
  const { showToast } = useToast()
  const stripeRef = useRef<Stripe | null>(null)
  const elementsRef = useRef<StripeElements | null>(null)
  const [orderId, setOrderId] = useState<string>()
  const [paymentError, setPaymentError] = useState<string | undefined>()
  const {
    data: savedCardsInfo,
    isLoading: isSavedCardsInfoLoading,
    mutate: refetchSavedCardsInfo,
  } = useSWR(
    Boolean(customer?.id) && stripePaymentId && isAuthUser && !hideSavedCards
      ? `savedCardsInfo-${stripePaymentId}`
      : null,
    async () => {
      const { data: getSavedcardsResponse } = await OrderServices.getSavedCardsOfCustomer(
        customer?.id ?? "",
        stripePaymentId
      )
      if (getSavedcardsResponse) {
        return getSavedcardsResponse
      }
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      refreshWhenOffline: false,
      refreshWhenHidden: false,
      refreshInterval: 0,
      shouldRetryOnError: false,
    }
  )

  const defaultCardId = useMemo(() => {
    if (savedCardsInfo?.length) {
      const id = savedCardsInfo.find((card) => card.is_default)?.id
      return id || savedCardsInfo[0].id
    }
    return ""
  }, [savedCardsInfo])

  const initialValues: StripeCreditCardFormAttributes = useMemo(
    () => ({
      savedCardId: defaultCardId,
      name: "",
      paymentType: paymentAmountDetails?.isDepositEnabledForItems
        ? PaymentTypes.DEPOSIT_AMOUNT
        : PaymentTypes.FULL_AMOUNT,
      amount: giftcardDetails
        ? paymentAmountDetails?.isDepositEnabledForItems
          ? `${giftcardDetails.remaining_deposit_total.amount_in_units}`
          : `${giftcardDetails.remaining_total.amount_in_units}`
        : `${
            paymentAmountDetails?.isDepositEnabledForItems
              ? paymentAmountDetails?.depostAmountWithTip
              : (paymentAmountDetails?.totalAmountWithTip ?? 0)
          }`,
      isUsingSavedCard: Boolean(savedCardsInfo?.length),
      hasPaymentValidData: true,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [giftcardDetails, savedCardsInfo, paymentAmountDetails]
  )

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        name: Yup.string().when("savedCardId", (savedCardId, field) =>
          !savedCardId ? field.required("*Card Name is Required") : field
        ),
        paymentType: Yup.string().required("*Payment Type is Required"),
        amount: Yup.string()
          .matches(/^\d{1,}(\.\d{0,4})?$/, "Amount is not valid")
          .required("*Amount is Required"),
        isUsingSavedCard: Yup.boolean().optional(),
      }),
    []
  )

  const handle3DSResult = async (
    values: StripeCreditCardFormAttributes,
    payload: CreateOrderRequestType | PayOrderRequestType,
    token?: SetupIntent | PaymentIntent
  ) => {
    if (token?.id) {
      if (postPaymentOrderId && handleCustomerRebookDuePayment) {
        await handleCustomerRebookDuePayment(payload)
      } else {
        const { data: successful3dsResponse } = postPaymentOrderId
          ? await OrderServices.payOrder(
              {
                ...payload,
                payment_intent_id: token.id,
                order_id: postPaymentOrderId,
              },
              isPublicPayment
            )
          : await OrderServices.createOrder(
              {
                ...(payload as CreateOrderRequestType),
                payment_intent_id: token.id,
                order_id: orderId,
              },
              isIntegratedPage
            )
        if (OrderStatuses.includes(successful3dsResponse?.order?.status as OrderStatusTypes)) {
          if (isIntegratedPage) {
            onOrderCreation?.(successful3dsResponse.order)
          } else {
            showToast({
              heading: (
                <div className={"flex flex-row items-center justify-between"}>
                  {values.paymentType === PaymentTypes.SAVE_CARD
                    ? "Card saved successfully!"
                    : "Payment successful!"}
                </div>
              ),
            })
            onSuccess?.(successful3dsResponse.order.id)
          }
        } else {
          setPaymentError(successful3dsResponse.message)
          onError?.()
        }
      }
    } else {
      setPaymentError(
        `${values.paymentType === PaymentTypes.SAVE_CARD ? "Failed to save card!" : "Payment failed!"} please try again.`
      )
      onError?.()
    }
  }

  const onSubmit = async (
    values: StripeCreditCardFormAttributes,
    { setSubmitting }: FormikHelpers<StripeCreditCardFormAttributes>
  ) => {
    try {
      setSubmitting(true)
      if (!stripeRef.current || !elementsRef.current) return

      const cardNumberElement = elementsRef.current?.getElement(CardNumberElement)
      if (cardNumberElement || Boolean(values.savedCardId)) {
        setPaymentError("")

        const selectedCard = savedCardsInfo?.find(
          (savedCard) => savedCard.id === values.savedCardId
        )
        const nameOnCard = values.isUsingSavedCard ? selectedCard?.name_on_card : values.name
        let paymentMethodToken = selectedCard?.payment_token
        // Create payment method only when the new card is used
        if (!values.savedCardId) {
          const { paymentMethod, error } = await stripeRef.current.createPaymentMethod({
            type: "card",
            card: cardNumberElement,
            billing_details: {
              email: customer?.email,
              phone: customer?.phone
                ? `${customer.phone_code ? `+${customer.phone_code}` : ""} ${customer.phone}`
                : "",
            },
          })
          if (error) {
            setPaymentError(
              error.message ?? "Unable to create payment token! please try again later"
            )
          }
          paymentMethodToken = paymentMethod?.id
        }
        if (handlePaymentConfirmation && (handleSubmit || paymentMethodToken)) {
          showPendingBookingPaymentConfirmation?.(true)
          return
        }
        if (handleSubmit && paymentMethodToken) {
          handleSubmit(
            {
              ...values,
              amount: giftcardDetails
                ? `${paymentAmountDetails?.totalAmountWithTip ?? 0}`
                : values.amount,
            },
            paymentMethodToken,
            stripeRef.current
          )
        } else if (paymentMethodToken) {
          const createOrPayOrderPayload: CreateOrderRequestType | PayOrderRequestType = {
            payment_token: paymentMethodToken,
            payment_method: stripePaymentId,
            from_saved_card: values.isUsingSavedCard,
            amount: giftcardDetails
              ? paymentAmountDetails?.isDepositEnabledForItems
                ? `${paymentAmountDetails?.depostAmountWithTip}`
                : `${paymentAmountDetails?.totalAmountWithTip ?? 0}`
              : onlyTipPayment
                ? 0
                : values.amount,
            payment_type: values.paymentType,
          }
          if (nameOnCard) {
            createOrPayOrderPayload.name_on_card = nameOnCard
          }
          if (cartId) {
            createOrPayOrderPayload.cart_id = cartId
          }
          if (postPaymentOrderId) {
            createOrPayOrderPayload.order_id = postPaymentOrderId
            createOrPayOrderPayload.tip = payOrderTipDetails
          }
          if (orderId) createOrPayOrderPayload.order_id = orderId
          if (giftcardDetails?.code) createOrPayOrderPayload.gift_card_code = giftcardDetails?.code
          if (postPaymentOrderId && handleCustomerRebookDuePayment) {
            await handleCustomerRebookDuePayment(createOrPayOrderPayload)
            setSubmitting(false)
          } else {
            const { data: createOrPayOrderResponse } = postPaymentOrderId
              ? await OrderServices.payOrder(
                  createOrPayOrderPayload as PayOrderRequestType,
                  isPublicPayment
                )
              : await OrderServices.createOrder(
                  createOrPayOrderPayload as CreateOrderRequestType,
                  isIntegratedPage
                )
            if (createOrPayOrderResponse.code === "card_3d_secure_required") {
              if (createOrPayOrderResponse?.order?.id) {
                createOrPayOrderPayload.order_id = createOrPayOrderResponse.order.id
                setOrderId(createOrPayOrderResponse.order.id)
              }
              if (values.paymentType === PaymentTypes.SAVE_CARD) {
                const { setupIntent: setupToken, error } = await stripeRef.current.confirmCardSetup(
                  createOrPayOrderResponse.payload?.client_secret ?? "",
                  {
                    payment_method: paymentMethodToken,
                  }
                )

                if (error) {
                  setPaymentError(error.message)
                } else {
                  await handle3DSResult(values, createOrPayOrderPayload, setupToken)
                }
              } else {
                const { paymentIntent: paymentToken, error } =
                  await stripeRef.current.confirmCardPayment(
                    createOrPayOrderResponse.payload?.client_secret ?? "",
                    {
                      payment_method: paymentMethodToken,
                    }
                  )

                if (error) {
                  setPaymentError(error.message)
                } else {
                  await handle3DSResult(values, createOrPayOrderPayload, paymentToken)
                }
              }
            } else {
              if (
                OrderStatuses.includes(createOrPayOrderResponse?.order?.status as OrderStatusTypes)
              ) {
                if (isIntegratedPage) {
                  onOrderCreation?.(createOrPayOrderResponse.order)
                } else {
                  showToast({
                    heading: (
                      <div className={"flex flex-row items-center justify-between"}>
                        {values.paymentType === PaymentTypes.SAVE_CARD
                          ? "Card saved successfully!"
                          : "Payment successful!"}
                      </div>
                    ),
                  })
                  onSuccess?.(createOrPayOrderResponse?.order?.id)
                  onOrderCreation?.()
                }
              } else {
                setPaymentError(createOrPayOrderResponse.message)
                onError?.()
                if (createOrPayOrderResponse?.order?.id) {
                  setOrderId(createOrPayOrderResponse?.order?.id)
                }
              }
            }
          }
        } else {
          onError?.()
        }
      } else {
        const createOrPayOrderPayload: CreateOrderRequestType | PayOrderRequestType = {
          payment_type: values.paymentType,
          cart_id: cartId,
        }

        const { data: createOrPayOrderResponse } = await OrderServices.createOrder(
          createOrPayOrderPayload,
          isIntegratedPage
        )

        if (OrderStatuses.includes(createOrPayOrderResponse?.order?.status as OrderStatusTypes)) {
          if (isIntegratedPage) {
            onOrderCreation?.(createOrPayOrderResponse.order)
          } else {
            showToast({
              heading: (
                <div className={"flex flex-row items-center justify-between"}>
                  Payment successful!
                </div>
              ),
            })
            onSuccess?.(createOrPayOrderResponse?.order?.id)
            onOrderCreation?.()
          }
        }
      }
    } catch (e: any) {
      if (e?.response?.data?.payload?.order_id) {
        setOrderId(e.response.data.payload.order_id)
      }
      Sentry.captureException(e)
      setPaymentError(
        e?.response?.data?.message ??
          e?.response?.data?.error?.message ??
          e?.message ??
          "Something went wrong! Please try again later."
      )
      onError?.()
    }
  }

  const handleWalletPayment = async (values: StripeCreditCardFormAttributes) => {
    try {
      if (!stripeRef.current || !elementsRef.current) return

      const { error: submitError } = await elementsRef.current.submit()
      if (submitError) {
        setPaymentError(submitError.message)
        Sentry.captureException(submitError)
        return
      }

      const { error: confirmationTokenError, confirmationToken } =
        await stripeRef.current.createConfirmationToken({
          elements: elementsRef.current,
        })

      if (confirmationTokenError) {
        setPaymentError(confirmationTokenError.message)
        Sentry.captureException(confirmationTokenError)
        return
      }

      const createOrPayOrderPayload: CreateOrderRequestType | PayOrderRequestType = {
        payment_token: `${confirmationToken?.id}`,
        payment_method: stripePaymentId,
        from_saved_card: false,
        amount: giftcardDetails
          ? paymentAmountDetails?.isDepositEnabledForItems
            ? `${paymentAmountDetails?.depostAmountWithTip}`
            : `${paymentAmountDetails?.totalAmountWithTip ?? 0}`
          : onlyTipPayment
            ? 0
            : values.amount,
        payment_type: values.paymentType,
      }

      if (cartId) {
        createOrPayOrderPayload.cart_id = cartId
      }
      if (postPaymentOrderId) {
        createOrPayOrderPayload.order_id = postPaymentOrderId
        createOrPayOrderPayload.tip = payOrderTipDetails
      }
      if (orderId) createOrPayOrderPayload.order_id = orderId
      if (giftcardDetails?.code) createOrPayOrderPayload.gift_card_code = giftcardDetails?.code

      const { data: createOrPayOrderResponse } = postPaymentOrderId
        ? await OrderServices.payOrder(
            createOrPayOrderPayload as PayOrderRequestType,
            isPublicPayment
          )
        : await OrderServices.createOrder(
            createOrPayOrderPayload as CreateOrderRequestType,
            isIntegratedPage
          )

      if (createOrPayOrderResponse.code === "card_3d_secure_required") {
        if (createOrPayOrderResponse?.order?.id) {
          createOrPayOrderPayload.order_id = createOrPayOrderResponse.order.id
          setOrderId(createOrPayOrderResponse.order.id)
        }

        const { error, paymentIntent: paymentToken } = await stripeRef.current.handleNextAction({
          clientSecret: createOrPayOrderResponse.payload?.client_secret ?? "",
        })

        if (error) {
          setPaymentError(error?.message ?? "Payment failed! please try again.")
          onError?.()
        }

        await handle3DSResult(values, createOrPayOrderPayload, paymentToken)
      } else {
        if (OrderStatuses.includes(createOrPayOrderResponse?.order?.status as OrderStatusTypes)) {
          onSuccess?.(createOrPayOrderResponse.order.id)
          onOrderCreation?.(createOrPayOrderResponse.order)
        } else {
          setPaymentError(createOrPayOrderResponse.message)
          onError?.()
          if (createOrPayOrderResponse?.order?.id) {
            setOrderId(createOrPayOrderResponse.order.id)
          }
        }
      }
    } catch (error: any) {
      console.error(error)
      Sentry.captureException(error)
      setPaymentError(
        error?.response?.data?.message ??
          error?.response?.data?.error?.message ??
          error?.message ??
          "Something went wrong! Please try again later."
      )
      if (error?.response?.data?.payload?.order_id) {
        setOrderId(error.response.data.payload.order_id)
      }
    }
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      validateOnChange
      enableReinitialize
      innerRef={stripeCreditCardFormRef}
      onSubmit={onSubmit}
    >
      {({ values }) => (
        <Form
          autoComplete={"off"}
          onKeyDown={(e) => {
            if (e.key === "Enter") e.preventDefault()
          }}
        >
          {publicDomainDetails.currency_iso3 ? (
            <Elements
              stripe={getStripe(stripeKey)}
              options={
                isWalletsEnabled
                  ? {
                      mode: "payment",
                      currency: publicDomainDetails.currency_iso3.toLowerCase(),
                      amount:
                        values.paymentType === PaymentTypes.DEPOSIT_AMOUNT
                          ? paymentAmountDetails?.depositAmountWithTipInCents
                          : paymentAmountDetails?.totalAmountWithTipInCents,
                      paymentMethodTypes: ["card"],
                    }
                  : {}
              }
            >
              {isSavedCardsInfoLoading ? (
                <div className={"w-full flex items-center justify-center"}>
                  <Spinner
                    aria-label={"Center-aligned spinner example"}
                    className={"text-center my-6"}
                  />
                </div>
              ) : (
                <StripeCreditCardFormData
                  handleClose={handleClose}
                  hideDefaultContainer={hideDefaultContainer}
                  cartId={cartId}
                  isIntegratedPage={isIntegratedPage}
                  cartDetails={cartDetails}
                  refetchCartData={refetchCartData}
                  paymentAmountDetails={paymentAmountDetails}
                  goToPrevStep={goToPrevStep}
                  hideBackButton={hideBackButton}
                  payOrderTipDetails={payOrderTipDetails}
                  onlyTipPayment={onlyTipPayment}
                  customer={customer}
                  isPublicPayment={isPublicPayment}
                  giftcardDetails={giftcardDetails}
                  isWidgetsCard={isWidgetsCard}
                  savedCardsInfo={savedCardsInfo}
                  onSavedCardDelete={() => refetchSavedCardsInfo()}
                  error={paymentError}
                  stripeRef={stripeRef}
                  elementsRef={elementsRef}
                  isWalletsEnabled={isWalletsEnabled}
                  handleWalletPayment={handleWalletPayment}
                />
              )}
            </Elements>
          ) : null}
        </Form>
      )}
    </Formik>
  )
}

export default StripeCreditCard
